import { type ClassValue, clsx } from "clsx";
import * as CSV from "csv-string";
import numeral from "numeral";
import { twMerge } from "tailwind-merge";

import { Prices } from "@/app/market/table";
import { Asset as TemplateAsset } from "@/app/smart-allocation/templates/types";
import { Asset } from "@/app/smart-allocation/types";
import {
  chartDefaultColorsHex,
  coinsColors,
} from "@/services/constants/coins/coinsColors";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(input: string | number): string {
  const date = new Date(input);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export const formatNumber = (
  number: number,
  isUSD = false,
  withSign = false,
  rate: number = 1,
  symbol: string = "$",
) => {
  const sign = number > 0 ? "+" : number === 0 ? "" : "-";
  number = Math.abs(number);
  if (isNaN(rate)) {
    rate = 1;
  }
  if (number) {
    if (Math.abs(number) < 1) {
      if (Math.abs(number) < 0.000001) {
        return (
          (isUSD ? (withSign ? sign : "") + symbol : withSign ? sign : "") +
          "<" +
          0.000001
        );
      }
      return (
        (isUSD ? (withSign ? sign : "") + symbol : withSign ? sign : "") +
        numeral(isUSD ? number * rate : number).format("0.0[00000]")
      );
    }
    if ((isUSD ? number * rate : number) < 1e9) {
      return (
        (isUSD ? (withSign ? sign : "") + symbol : withSign ? sign : "") +
        numeral(isUSD ? number * rate : number).format("0,0[.]00")
      );
    } else {
      return (
        (isUSD ? (withSign ? sign : "") + symbol : withSign ? sign : "") +
        numeral(isUSD ? number * rate : number)
          .format("0,0[.]00a")
          .toUpperCase()
      );
    }
  } else if (isUSD) {
    return (symbol ?? "$") + "0";
  }
  return "0";
};

export const percentageFormat = (number: number, withSign = false) => {
  if (number) {
    const sign = number > 0 ? "+" : number === 0 ? "" : "-";
    if (Math.abs(number) < 0.01) {
      if (Math.abs(number) < 0.001) {
        return (withSign ? sign : "") + "<0.001";
      } else {
        return (
          (withSign ? sign : "") + numeral(Math.abs(number)).format("0.0[0000]")
        );
      }
    } else {
      return (
        (withSign ? sign : "") + numeral(Math.abs(number)).format("0a[.]00")
      );
    }
  }
  return 0;
};

export const textColorBasedOnNumber = (number: number) => {
  if (number === 0) return "";
  else if (number > 0) return "text-success dark:text-green-600";
  else return "text-destructive dark:text-red-600";
};

export const shortNumberFormat = (number: number) => {
  if (number < 1000) {
    if (number < 1 && number > 0) {
      if (number < 0.0001) {
        return "<0.0001";
      }
      return numeral(number).format("0.000");
    }
    return numeral(number).format("0[.]00a");
  } else {
    return numeral(number)
      .format("0.0a", (n: number) => {
        return Math.floor(n);
      })
      .toUpperCase();
  }
};

function shadeColor(color: string, percent: number) {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  R = parseInt(`${(R * (100 + percent)) / 100}`);
  G = parseInt(`${(G * (100 + percent)) / 100}`);
  B = parseInt(`${(B * (100 + percent)) / 100}`);

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  R = Math.round(R);
  G = Math.round(G);
  B = Math.round(B);

  let RR = R.toString(16).length === 1 ? "0" + R.toString(16) : R.toString(16);
  let GG = G.toString(16).length === 1 ? "0" + G.toString(16) : G.toString(16);
  let BB = B.toString(16).length === 1 ? "0" + B.toString(16) : B.toString(16);

  return "#" + RR + GG + BB;
}

export function getCoinColor(symbol: string, defaultColorIndex: number) {
  const shadePercent = -4;
  const defaultColor =
    chartDefaultColorsHex[defaultColorIndex % chartDefaultColorsHex.length];
  if (symbol) {
    return shadeColor(
      coinsColors[symbol?.toLowerCase()] ?? defaultColor,
      shadePercent,
    );
  } else {
    return shadeColor(defaultColor, shadePercent);
  }
}

export function applyLocalTimezoneOffset(timestamp: number) {
  const localTimezoneOffset = new Date().getTimezoneOffset();
  const timestampOffset = localTimezoneOffset * 60 * 1000;
  return timestamp - 2 * timestampOffset;
}

export function getShiftedDayDate(prevDay: Date, shiftInDays: number) {
  return new Date(
    prevDay.getFullYear(),
    prevDay.getMonth(),
    prevDay.getDate() + shiftInDays,
  );
}

export function sortSmartAllocationsHoldings(a: Asset, b: Asset, base: string) {
  // Sort USDT first
  if (a.name === base) {
    return -1;
  } else if (b.name === base) {
    return 1;
  }

  // Sort other stable coins last
  if (a.stable && !b.stable) {
    return 1;
  } else if (!a.stable && b.stable) {
    return -1;
  }

  // Sort the rest of coins based on their weight
  return (b.weight ?? 0) - (a.weight ?? 0);
}

export const parseSmartAllocationHolding = (selectedAsset: TemplateAsset) => {
  return {
    name: selectedAsset.name.toUpperCase() ?? "",
    weight: selectedAsset.weight,
    current_weight: 0,
    available: 0,
    current_value: 0,
    executable: true,
    ask_price: selectedAsset?.current_price,
    marketcap: selectedAsset?.market_cap,
    rank: selectedAsset?.market_cap_rank,
    asset_name: selectedAsset.asset_name,
    asset_details: {
      asset_data: {
        name: selectedAsset?.name ?? "",
        image: selectedAsset?.image,
        price_change_24h: selectedAsset?.price_change_24h,
        price_change_percentage_24h: selectedAsset?.price_change_percentage_24h,
        current_price: selectedAsset?.current_price,
      },
    },
  };
};

export function upsert(array: Prices[], element: Prices) {
  // (1)
  const i = array.findIndex((e) => e.symbol === element.symbol);
  if (i > -1) array[i] = element; // (2)
  else array.push(element);
}

export const downloadFile = ({ list, fileName, fileType }: any) => {
  const arr = CSV.parse(list as any);
  const data = arr.join("\n");
  const blob = new Blob([data], { type: fileType });
  const a = document.createElement("a");
  a.download = fileName;
  a.href = window.URL.createObjectURL(blob);
  const clickEvt = new MouseEvent("click", {
    view: window,
    bubbles: true,
    cancelable: true,
  });
  a.dispatchEvent(clickEvt);
  a.remove();
};
